<template>
    <div class="search-profile">
        <h4>Search for the <i :class="getProfileIcon(profileType)"/> {{ prettyProfileType(profileType) }} you would like to add</h4>

        <slot name="description"></slot>

        <div class="search-profile__container">
            <div class="search-profile__details">
                Enter search details
                <input style="width: 85%" type="text" :placeholder="searchPlaceholder ? searchPlaceholder : 'Profile name'" v-model="searchHandle" @keydown.enter="search()"/>
                <be-button @click="search" :disabled="searchHandle === '' || searching"><i class="icon-search"/>Search for {{ prettyProfileType(profileType) }}</be-button>
            </div>
            <div class="search-profile__divider"></div>
            <div class="search-profile__results">
                <loading-message style="margin-top: 0" v-if="searching" message="Searching..."/>
                <div v-else>
                    Search Results
                    <online-profile class="search-profile__profile" v-if="newProfileData.publicProfile" :profile="newProfileData.publicProfile"/>
                    <p class="search-profile__search-error" v-if="searchError">
                        {{ searchError }}
                    </p>
                </div>
            </div>
        </div>

        <div v-if="newProfileData.publicProfile && newProfileData.publicProfile.existing && !newProfileData.publicProfile.existing.deleted" class="search-profile__error-message">
            <i class="symbol-warning"></i>
            <p>
                This profile has already been added to account {{ account.name }}. You are only able to add profiles that have not been added to the account yet.
            </p>
        </div>

        <!-- A deleted profile is restored in place instead of being rebuilt through the rest of the wizard, so that
             everything already attached to it (tags, brands, authorisation) is kept -->
        <div v-else-if="newProfileData.publicProfile && newProfileData.publicProfile.existing" class="search-profile__restore-message">
            <div class="search-profile__restore-text">
                <i :class="restoreUnavailable ? 'symbol-warning' : 'symbol-info'"></i>
                <p v-if="restoreUnavailable">
                    This profile was added to account {{ account.name }} before and then deleted, but we were unable to
                    load the account's copy of it. Please reload the page and try again, or contact support if this
                    keeps happening.
                </p>
                <p v-else>
                    This profile was added to account {{ account.name }} before and then deleted. Restore it to bring it
                    back with the tags, brands and authorisation it already had.
                </p>
            </div>
            <be-button v-if="!restoreUnavailable" primary :disabled="restoring || loadingAccountProfiles" @click="restoreProfile">
                <spinner-component v-if="restoring" :size="11"/>{{ restoring ? 'Restoring...' : 'Restore profile' }}
            </be-button>
        </div>
    </div>
</template>

<script>
import OnlineProfile from "@/app/framework/dialogs/user-settings/views/platform-auth/OnlineProfile";
import {beef} from "@/store/Services";
import {extractHandleId, getProfileIcon, prettyProfileType, profileTypes} from "@/setup/profiles/ProfileUtils";
import BeButton from "@/components/buttons/BeButton";
import LoadingMessage from "@/components/LoadingMessage";
import SpinnerComponent from "@/components/SpinnerComponent";
import VuexStore from "@/store/vuex/VuexStore";
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import {notifyUserOfError, notifyWithText} from "@/app/framework/notifications/Notifications";

export default {
    name: "SearchProfile",
    store: VuexStore,
    components: {LoadingMessage, BeButton, OnlineProfile, SpinnerComponent},
    props: {
        profileType: {
            required: true,
            type: String
        },
        extractHandleId: {
            default: true,
            type: Boolean
        },
        searchPlaceholder: String
    },

    data: function() {
        return {
            searching: false,
            restoring: false,
            loadingAccountProfiles: false,
            searchHandle: "",
            searchError: "",
            profileTypes: profileTypes
        }
    },

    computed: {
        ...mapState(['account']),
        ...mapState('profiles', ['newProfileData', 'profiles']),
        ...mapGetters('profiles', ['handleIdToProfile']),

        /**
         * The account's copy of the profile that has been found, when that profile has been deleted. The lookup the
         * search runs only reports that a deleted profile exists; the id needed to restore it comes from the profile
         * list, which is loaded with deleted profiles included.
         */
        existingDeletedProfile() {
            let publicProfile = this.newProfileData.publicProfile;
            if (!publicProfile?.existing?.deleted) return null;

            return this.profiles?.find(profile => profile.type === publicProfile.type
                && profile.handleId === publicProfile.handleId && profile.deleted) ?? null;
        },

        /**
         * Whether the deleted profile that was found cannot be restored from here, because the account's copy of it
         * could not be loaded. The user is told so rather than being left with a disabled button.
         */
        restoreUnavailable() {
            return !this.loadingAccountProfiles && !this.existingDeletedProfile;
        }
    },

    methods: {
        ...mapMutations('profiles', ['setNewPublicProfile']),
        ...mapActions('profiles', {
            refreshProfiles: 'refreshProfiles',
            storeUndeleteProfile: 'undeleteProfile'
        }),
        getProfileIcon,
        prettyProfileType,

        /**
         * Loads the account's profiles, which the restore needs for its own copy of the profile that was found. The
         * list is always fetched fresh: a cached copy can predate the deletion that the search has just reported,
         * which would leave the profile missing from it.
         */
        async loadAccountProfiles() {
            try {
                this.loadingAccountProfiles = true;
                await this.refreshProfiles(true);
            } catch (e) {
                console.error("An error occurred while fetching the account's profiles: ", e);
            } finally {
                this.loadingAccountProfiles = false;
            }
        },

        /**
         * Restores the deleted profile that the search found, rather than adding a new one. This is the same operation
         * as the Undelete button on a profile row, so the profile keeps everything it had before it was deleted.
         */
        async restoreProfile() {
            let profile = this.existingDeletedProfile;
            if (!profile || this.restoring) return;

            try {
                this.restoring = true;

                // the store action refetches the profile and commits it, so the list needs no further refresh here.
                // Refreshing inside this try would also report a successful restore as a failure if the refresh threw.
                await this.storeUndeleteProfile({profileId: profile.id, linkedProfileId: profile.linkedPresenceId});

                notifyWithText(`${prettyProfileType(profile.type)} ${profile.name ?? profile.handle} has been restored.`,
                    null, "<i class='symbol-profiles'></i>");

                // the profile is no longer deleted, so the search result it came from no longer applies
                this.setNewPublicProfile(null);

                this.$emit('restored');
            } catch (e) {
                console.error(`An error occurred while restoring profile ${profile.id}: `, e);
                notifyUserOfError("We were unable to restore this profile. Please contact support if this keeps happening.");
            } finally {
                this.restoring = false;
            }
        },

        async search() {
            const MAX_POLL_ATTEMPTS = 8;
            const POLL_INTERVAL_MS = 15_000;

            try {
                this.searching = true;
                this.profileExists = false;
                this.setNewPublicProfile(null);

                let lookup = this.extractHandleId ? extractHandleId(this.searchHandle) : this.searchHandle;
                let url = `/api/online-profile/validate?code=${this.account.code}&type=${this.profileType}&handle=${lookup}`;

                for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
                    let response = await beef.get(url);

                    // Async lookups (currently Trustpilot) return an in_progress status while the
                    // background worker is still fetching. Wait and try again.
                    if (response.data.status === "in_progress") {
                        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
                        continue;
                    }

                    // Terminal state.
                    if (response.data.message) {
                        this.setNewPublicProfile(null);
                        this.searchError = response.data.message;
                    } else {
                        this.searchError = "";

                        let profile = response.data;

                        profile.media = null;
                        profile.tagIds = [];
                        profile.relevant = false;
                        profile.description = response.data.description ??= "";
                        profile.type = this.profileType;


                    if (profile.existing?.deleted) await this.loadAccountProfiles();
                        this.setNewPublicProfile(profile);
                    }
                    return;
                }

                this.setNewPublicProfile(null);
                this.searchError = "Lookup is taking longer than expected, please try again";
            } catch(e) {
                console.error(`An error occurred while searching for profile ${this.searchHandle}: `, e);

                this.setNewPublicProfile(null);
                this.searchError = "An error occurred while searching";
            } finally {
                this.searching = false;
            }
        },
    }
}
</script>

<style scoped lang="sass">

.search-profile

    h4
        margin-bottom: 20px

    p
        color: var(--be-colour-text-dark)

    &__container
        display: flex
        background: #444
        border: 1px solid #272727
        padding: 10px
        margin-top: 20px
        margin-bottom: 30px
        border-radius: 4px

    &__search-error
        color: var(--be-colour-warning) !important
        word-break: break-word

    &__error-message
        color: var(--be-colour-warning)
        display: flex
        align-items: center

        i
            margin-right: 10px

        p
            margin: 0

    &__restore-message
        display: flex
        align-items: center
        column-gap: 20px

    &__restore-text
        color: var(--be-colour-muted-text-dark)
        display: flex
        align-items: center

        i
            margin-right: 10px

        p
            margin: 0
            color: var(--be-colour-muted-text-dark) !important

    &__details
        display: flex
        flex-direction: column
        row-gap: 10px
        width: 50%

    &__divider
        margin: -10px 0
        border-right: 1px dashed #272727

    &__results
        display: flex
        flex-direction: column
        row-gap: 10px
        width: 50%
        padding-left: 10px

    &__profile
        font-size: 14px
        margin-top: 5px

        ::v-deep img
            width: 50px
            height: 50px


</style>